import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, MessageSquare, Trash2, ArrowLeft } from 'lucide-react';
import { ChatMessage, ChatLoading, SuggestedQuestions } from '../components/chat/ChatComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async (text) => {
    const content = text || input.trim();
    if (!content) return;

    const userMsg = { role: 'user', content };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/chat`, {
        query: content,
        history: [...messages, userMsg].slice(-10)
      }, { withCredentials: true });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response || 'Nessuna risposta',
        visualization: res.data.visualization || null
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Errore nella comunicazione con il server.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]" data-testid="chat-page">
      {/* Header */}
      <div className="border-b border-[#27272A] p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-[#A1A1AA] hover:text-white transition-colors p-1" data-testid="chat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <MessageSquare className="w-6 h-6 text-[#E3000F]" />
          <div>
            <h1 className="text-lg font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Assistente AI
            </h1>
            <p className="text-xs text-[#71717A]">Cerca informazioni sugli interventi</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={() => setMessages([])}
            className="text-[#71717A] hover:text-[#E3000F] transition-colors p-2"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-6">
            <MessageSquare className="w-16 h-16 text-[#27272A]" />
            <p className="text-[#71717A] text-center">Chiedi informazioni sugli interventi</p>
            <SuggestedQuestions onSelect={sendMessage} />
          </div>
        ) : (
          messages.map((msg, i) => (
            <ChatMessage key={i} msg={msg} index={i} />
          ))
        )}
        {loading && <ChatLoading />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-[#27272A] p-4">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio..."
            className="flex-1 bg-[#1A1A1A] border border-[#27272A] focus:border-[#E3000F] text-white p-3 text-sm resize-none outline-none"
            rows={1}
            disabled={loading}
            data-testid="chat-input"
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="bg-[#E3000F] hover:bg-[#B8000C] disabled:opacity-50 text-white px-4 flex items-center justify-center transition-colors"
            data-testid="chat-send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
